import React, { useState, useContext } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/appContext";
import { API_BASE_URL } from "../apiConfig";
import "./EventModal.scss";

function EventModal({ visible, onClose, event, onRefresh }) {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { setCurrentRoom } = useContext(AppContext);
  const [loading, setLoading] = useState(false);

  if (!event) return null;

  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  const isPast = event.date < today;
  // Store and check User ObjectIds instead of raw usernames (support id/id fallback for active web sessions)
  const isRegistered = event.participants && user && event.participants.some((p) => String(p._id || p) === String(user._id || user.id));
  const maxParticipants = event.maxParticipants || 0;
  const currentParticipantsCount = event.participants ? event.participants.length : 0;
  const isFull = maxParticipants > 0 && currentParticipantsCount >= maxParticipants;

  const handleRegister = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/events/${event._id}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user._id || user.id }),
      });
      if (response.ok) {
        onRefresh();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to register");
      }
    } catch (e) {
      console.error(e);
      alert("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleLeave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/events/${event._id}/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user._id || user.id }),
      });
      if (response.ok) {
        onRefresh();
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to leave");
      }
    } catch (e) {
      console.error(e);
      alert("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinChat = () => {
    setCurrentRoom(`room_event_${event._id}`);
    onClose();
    navigate("/chat");
  };

  return (
    <Modal
      show={visible}
      onHide={onClose}
      centered
      className="custom-event-modal"
      contentClassName="custom-modal-content"
    >
      <div className="close-button-container">
        <button type="button" className="close-icon-btn" onClick={onClose} aria-label="Close">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>
      </div>

      <div className="header">
        <div className={`icon-container ${isPast ? "past-icon-container" : ""}`}>
          {isPast ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#F59E0B" viewBox="0 0 16 16">
              <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#3B82F6" viewBox="0 0 16 16">
              <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
            </svg>
          )}
        </div>
        <h2 className="modal-title">{event.title}</h2>
        <p className={`modal-date ${isPast ? "text-muted" : ""}`}>
          {event.date}{event.time ? ` · ${event.time}` : ""}
        </p>
      </div>

      <Modal.Body className="modal-body-custom">
        {isPast && (
          <div className="past-banner">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#F59E0B" className="bi bi-clock-fill" viewBox="0 0 16 16">
              <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
            </svg>
            <span className="past-banner-text">This event has ended</span>
          </div>
        )}

        <div className="details-container">
          <h3 className="details-title">About Event</h3>
          <p className="details-text">{event.description || event.details || "No details available."}</p>
        </div>

        <div className="stats-container">
          <div className="stat-box">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#6B7280" viewBox="0 0 16 16">
              <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816zM4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275zM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0zm3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
            </svg>
            <span className="stat-text">
              {currentParticipantsCount} / {maxParticipants} Joined
            </span>
          </div>
        </div>

        <div className="action-container d-flex flex-column gap-2">
          {isPast ? (
            <Button variant="light" className="custom-btn ended-btn" disabled>
              Event Has Ended
            </Button>
          ) : loading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : isRegistered ? (
            <>
              <Button variant="success" className="custom-btn chat-btn" onClick={handleJoinChat}>
                💬 Join Event Chat
              </Button>
              <Button variant="outline-danger" className="custom-btn leave-btn" onClick={handleLeave}>
                Leave Event
              </Button>
            </>
          ) : (
            <Button 
              variant="primary" 
              className={`custom-btn ${isFull ? "full-btn" : "register-btn"}`} 
              onClick={handleRegister} 
              disabled={isFull}
            >
              {isFull ? "Event is Full" : "Register Now"}
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default EventModal;
